<template>
  <Modal
    class="allocation-chart-modal"
    :is-open="isOpen"
    title=""
    size="md"
    @close="emitClose"
  >
    <section v-if="loading" class="allocation-chart-modal__section">
      <HeadingDisplayLoader
        class="allocation-chart-modal__loader__heading"
        size="extra-small"
        :has-head="false"
      />
      <SkeletonLoader>
        <div class="allocation-chart-modal__loader__chart" />
        <div
          v-for="number in 5"
          :key="number"
          class="allocation-chart-modal__loader__row"
        />
      </SkeletonLoader>
    </section>
    <section v-else class="allocation-chart-modal__section">
      <HeadingDisplay
        title="Alocação em Bolsa"
        :subtitle="updatedAt ? updatedAt.formatted : '-'"
        size="extra-small"
      />
      <AllocationChart
        class="allocation-chart-modal__chart"
        :series-data="seriesDataPieChart"
        :title="titleChart"
        :title-percentage="titlePercentage"
        :title-total-value="titleTotalValue"
        :error="error"
        :selected-asset="selectedAsset"
        @select-filter="selectAssetType"
      />
      <div
        v-if="chartDataAssetTypes && !error && hasChartData"
        class="allocation-chart-modal__list"
      >
        <Row
          v-for="(asset, index) in chartDataAssetTypes"
          :key="index"
          class="allocation-chart-modal__list__item"
          :data-testid="`asset-item-${asset.assetType.value}`"
          has-interaction
          @click="
            selectAssetType({
              assetType: asset,
              options: {
                label: asset.assetType.formatted,
                value: asset.assetType.value
              }
            })
          "
        >
          <template #icon-left>
            <div class="allocation-chart-modal__item__status">
              <BulletAsset
                :color="
                  asset.color || getChartCategoryColor(asset.assetType.value)
                "
                class="allocation-chart-modal__item__status__bullet"
              />
              <BaseText
                as="p"
                size="lg"
                class="allocation-chart-modal__item__status__title"
              >
                {{ asset.assetType.formatted }}
              </BaseText>
            </div>
          </template>
          <template #top-right>
            <Money class="allocation-chart-modal__list__money" width="40px">{{
              asset.percentage.formatted
            }}</Money>
          </template>
          <template #bottom-right>
            <Money class="allocation-chart-modal__list__money" width="110px">{{
              asset.allocatedTotal.formatted
            }}</Money>
          </template>
        </Row>
      </div>
    </section>
  </Modal>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';

import { BulletAsset } from '@/modules/common/components/BulletAsset';
import { AllocationChart } from './components/AllocationChart';
import { Money } from '@/modules/common/components/MoneyV2';
import { PieChartData, SelectAssetType } from './types';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { CategoryColor } from './enums';
import {
  BaseText,
  Row,
  SkeletonLoader,
  Modal,
  HeadingDisplay
} from '@warrenbrasil/nebraska-web';
import { HeadingDisplayLoader } from '@/modules/trade/components/HeadingDisplayLoader';
import {
  PositionsChart,
  AssetType,
  Instrument,
  InstrumentHistorical
} from '@/modules/trade/services/instruments';
import { NumberFormatted } from '@/modules/trade/types';

@Component({
  components: {
    BaseText,
    SkeletonLoader,
    AllocationChart,
    BulletAsset,
    Row,
    Money,
    Modal,
    HeadingDisplay,
    HeadingDisplayLoader
  }
})
export default class AllocationChartModal extends Vue {
  private selectedAsset: AssetType = {} as AssetType;
  public CategoryColor = CategoryColor;

  @Prop({ type: Object, required: false })
  readonly chartData?: PositionsChart;

  @Prop({ type: Boolean, required: true })
  readonly loading!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly error!: boolean;

  @Prop({ type: Array, required: false })
  readonly instruments?: Instrument[] | InstrumentHistorical[];

  @Prop({ type: Boolean, default: false })
  readonly isOpen!: boolean;

  @Prop({ type: Object, required: false })
  readonly updatedAt?: NumberFormatted;

  @Emit('close')
  public emitClose() {}

  get titleChart() {
    const totalSelected = this.selectedAsset?.assetType?.formatted;
    return totalSelected ? `Total em ${totalSelected}` : 'Total alocado';
  }

  get titlePercentage() {
    const totalAlocated = this.chartData?.allocatedTotal?.formatted || '-';
    return this.selectedAsset?.percentage?.formatted ?? totalAlocated;
  }

  get titleTotalValue() {
    const activesAlocatedQuantity = this.chartData?.amountAssetsTotal || '-';
    return (
      this.selectedAsset?.allocatedTotal?.formatted ??
      `em ${activesAlocatedQuantity} ativos`
    );
  }

  get NebraskaColors() {
    return NebraskaColors;
  }

  get hasChartData() {
    return (
      this.chartData?.amountAssetsTotal && this.chartData?.amountAssetsTotal > 0
    );
  }

  get seriesDataPieChart(): Array<PieChartData> {
    const pieChartData = (this.chartData?.assetTypes || []).map(assetType => {
      const categoryValue = assetType.assetType?.value;
      const categoryName = assetType.assetType?.formatted;
      const categoryNumber = assetType.allocatedTotal.value;
      const categoryColor = assetType.color;
      return {
        name: categoryName,
        color:
          categoryColor ||
          this.getChartCategoryColor(categoryValue as CategoryColor),
        category: categoryValue as CategoryColor,
        y: categoryNumber,
        z: 92.9
      };
    });
    if (!this.hasChartData) return [];
    return pieChartData;
  }

  get chartDataAssetTypes() {
    return this.chartData?.assetTypes;
  }

  public selectAssetType({ assetType }: SelectAssetType) {
    this.selectedAsset = assetType;
  }

  getChartCategoryColor(categoryColor: CategoryColor | string): NebraskaColors {
    switch (categoryColor) {
      case CategoryColor.Stock:
        return NebraskaColors.assetFixedIncome;
      case CategoryColor.Fii:
        return NebraskaColors.assetStocks;
      case CategoryColor.Etf:
        return NebraskaColors.benchmarkCdi;
      case CategoryColor.Bdr:
        return NebraskaColors.assetOthers;
      case CategoryColor.Others:
        return NebraskaColors.benchmarkIpca;

      default:
        return NebraskaColors.assetFixedIncome;
    }
  }
}
</script>

<style lang="less" scoped>
.allocation-chart-modal {
  ::v-deep .modal-content {
    text-align: left;
  }

  &__section {
    width: 100%;
  }

  &__chart {
    margin: 0 auto;
  }

  &__list {
    margin-top: @size-spacing-x600;
    width: 100%;

    &__money {
      margin-left: auto;
    }

    &__item {
      ::v-deep .wrapper {
        width: auto;
      }
    }
  }

  &__item {
    &__status {
      display: flex;
      &__title {
        margin-left: @size-spacing-x350;
      }
      &__bullet {
        align-self: center;
      }
    }
  }

  &__loader {
    &__heading {
      width: 50%;
    }

    &__chart {
      margin: 0 auto;
      height: 350px;
      width: 350px;
      border-radius: @size-radius-max;
      z-index: 0;
      margin-top: @size-spacing-x350;
      margin-bottom: @size-spacing-x900;
    }

    &__row {
      padding-top: @size-spacing-x800;
      height: 35px;
      margin: @size-spacing-x600 0;
      border-radius: @size-radius-x200;
      width: 100%;
    }
  }
}
</style>
